import { isSessionExpiredRedirect } from './session-expiry.state';
import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';

/** Payload emitted on errors$. Consumers can subscribe to show global error UI. */
export interface HttpErrorPayload {
  status: number;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class UiService {
  private readonly _lastError = signal<HttpErrorPayload | null>(null);
  readonly lastError = this._lastError.asReadonly();

  /** Observable bridge kept for existing subscribers (interceptor, Sentry). */
  readonly errors$ = toObservable(this._lastError).pipe(
    filter((e): e is HttpErrorPayload => e !== null),
  );

  readonly mobileSidebarOpen = signal(false);
  toggleMobileSidebar() {
    this.mobileSidebarOpen.update((v) => !v);
  }
  closeMobileSidebar() {
    this.mobileSidebarOpen.set(false);
  }

  private readonly _modalData = signal<unknown>(null);
  readonly modalData = this._modalData.asReadonly();

  // Built ONCE as a field initializer (an injection context). Building it
  // inside onModalDataChange() called toObservable() after construction, which
  // threw NG0203 ("toObservable() can only be used within an injection context")
  // every time a component subscribed.
  private readonly _modalData$ = toObservable(this._modalData).pipe(filter((d) => d !== null));

  /** Observable bridge kept for existing modal-component subscribers. */
  onModalDataChange(): Observable<unknown> {
    return this._modalData$;
  }

  emitModalData(data: unknown): void {
    this._modalData.set(data);
    setTimeout(() => this._modalData.set(null), 0);
  }

  emitError(payload: HttpErrorPayload): void {
    if (environment.production && environment.sentryDsn) {
      Sentry.captureException(new Error(`HTTP ${payload.status}: ${payload.message}`));
    }
    this._lastError.set(payload);
    setTimeout(() => this._lastError.set(null), 0);
  }

  constructor(
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  // The `action` parameter is intentionally ignored. Historically ~350 call
  // sites passed severity labels ('Error' / 'Success' / '') here, which Material
  // renders as a literal action BUTTON ("ERROR" / "SUCCESS") that only dismisses
  // the toast — confusing UX, since an action button should be an action, not a
  // label. The param is kept in the signature so existing call sites still
  // compile; we simply never forward it to MatSnackBar. Severity is conveyed by
  // the snackbar--success/error/info panel classes via NotificationService.
  /**
   * Resolve severity + message defensively:
   * - Hundreds of legacy call sites pass 'Success'/'Error' as the (otherwise
   *   unused) action param expecting it to style the toast — honor that unless
   *   an explicit `type` is given.
   * - Backend passthroughs (err?.error?.message) can be undefined/empty on
   *   network failures; never show a blank toast.
   */
  private resolveSnackbar(
    message: string,
    action?: string,
    type?: 'success' | 'error' | 'info',
  ): { msg: string; type: 'success' | 'error' | 'info' } {
    const resolved =
      type ?? (action === 'Success' ? 'success' : action === 'Error' ? 'error' : 'info');
    const trimmed = typeof message === 'string' ? message.trim() : '';
    const msg =
      trimmed ||
      (resolved === 'success'
        ? 'Completed successfully.'
        : 'Something went wrong. Please try again.');
    return { msg, type: resolved };
  }

  openSnackbar(message: string, action?: string, type?: 'success' | 'error' | 'info') {
    // A 401 redirect is in flight: the interceptor already showed the
    // "session expired" toast — don't let trailing error handlers replace it.
    if (isSessionExpiredRedirect()) return;
    const r = this.resolveSnackbar(message, action, type);
    this.matSnackBar.dismiss();
    this.matSnackBar.open(r.msg, undefined, {
      duration: r.type === 'error' ? 4000 : 2000,
      panelClass: ['snackbar', `snackbar--${r.type}`],
      verticalPosition: 'bottom',
      horizontalPosition: 'end',
    });
  }

  openSnackbarThen(message: string, action?: string, type?: 'success' | 'error' | 'info') {
    const r = this.resolveSnackbar(message, action, type);
    this.matSnackBar.dismiss();
    return this.matSnackBar
      .open(r.msg, undefined, {
        duration: r.type === 'error' ? 4000 : 2000,
        panelClass: ['snackbar', `snackbar--${r.type}`],
        verticalPosition: 'bottom',
        horizontalPosition: 'end',
      })
      .afterDismissed();
  }

  /**
   * Opens the CDK confirm dialog. All call sites use `#delete_modal` — the
   * Bootstrap/jQuery path has been fully removed. Returns `{ afterClose }` for
   * backward compatibility with existing call sites.
   */
  openModal(selector: string, data?: Record<string, unknown>) {
    const dialogData: ConfirmDialogData = {
      modalTitle: data?.['modalTitle'] as string | undefined,
      msg: data?.['msg'] as string | undefined,
      prompt: data?.['prompt'] as string | undefined,
      icon: data?.['icon'] as string | undefined,
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
      panelClass: 'dm-dialog-panel',
      width: '440px',
      maxWidth: '90vw',
      position: { top: '60px' },
      // Confirmation dialog for consequential actions (delete, logout, reject…).
      // Require an explicit Cancel/Confirm choice — no accidental dismiss via
      // backdrop click or Escape.
      disableClose: true,
    });
    return { afterClose: dialogRef.afterClosed() };
  }

  closeModal(_selector: string, _data?: unknown) {
    // CDK dialog closes itself — this method is a no-op kept for call-site compatibility.
  }
}
